/**
 * Notes calculator (#729): a deterministic arithmetic engine plus the labeled
 * calculation → note line contract.
 *
 * This module is product-neutral on purpose. Brunel Command Web is the first
 * consumer; Darius (and the native app) can reuse the same parser, number
 * formatting and note-line shape through their own UI adapters. Nothing here
 * knows about note storage, drawers, organizations or AI routing.
 *
 * Arithmetic is plain local evaluation. It never calls a model and must not be
 * presented as AI reasoning.
 *
 * Grammar:
 *   expression := term (('+' | '-') term)*
 *   term       := unary (('*' | '/') unary)*
 *   unary      := ('+' | '-') unary | postfix
 *   postfix    := primary '%'?
 *   primary    := number | '(' expression ')'
 *
 * `×`, `·`, `÷`, `−` and `–` are accepted as operator aliases. Commas
 * between digits are digit grouping (`1,250`). `n%` is `n / 100`, except when a
 * bare percentage is the right-hand side of `+`/`-`, where it is relative to the
 * left side, as on a desk calculator: `200 + 10%` is `220`.
 */

export const CALCULATOR_EXPRESSION_LIMIT = 500;
export const CALCULATION_LABEL_LIMIT = 120;
export const CALCULATION_UNIT_LIMIT = 24;

/** Results are rounded to this many significant digits so `0.1 + 0.2` is `0.3`. */
const SIGNIFICANT_DIGITS = 12;

type Operator = '+' | '-' | '*' | '/';

type CalculatorToken =
  | { readonly kind: 'number'; readonly value: number; readonly start: number; readonly end: number }
  | { readonly kind: 'operator'; readonly op: Operator; readonly start: number; readonly end: number }
  | { readonly kind: 'percent'; readonly start: number; readonly end: number }
  | { readonly kind: 'open' | 'close'; readonly start: number; readonly end: number };

export type CalculationEvaluation =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly error: string };

const OPERATOR_ALIASES: Readonly<Record<string, Operator>> = {
  '+': '+',
  '-': '-',
  '−': '-',
  '–': '-',
  '*': '*',
  '×': '*',
  '·': '*',
  '/': '/',
  '÷': '/',
};

class CalculationError extends Error {}

function tokenize(expression: string): CalculatorToken[] {
  const tokens: CalculatorToken[] = [];
  let index = 0;
  while (index < expression.length) {
    const char = expression[index]!;
    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    if (/[0-9.]/.test(char)) {
      const start = index;
      let raw = '';
      while (index < expression.length) {
        const next = expression[index]!;
        if (/[0-9.]/.test(next)) {
          raw += next;
          index += 1;
        } else if (next === ',' && /[0-9]/.test(expression[index - 1] ?? '') && /[0-9]/.test(expression[index + 1] ?? '')) {
          index += 1;
        } else {
          break;
        }
      }
      if (!/^(\d+\.?\d*|\.\d+)$/.test(raw)) throw new CalculationError(`“${raw}” is not a number.`);
      tokens.push({ kind: 'number', value: Number(raw), start, end: index });
      continue;
    }
    const op = OPERATOR_ALIASES[char];
    if (op !== undefined) tokens.push({ kind: 'operator', op, start: index, end: index + 1 });
    else if (char === '%') tokens.push({ kind: 'percent', start: index, end: index + 1 });
    else if (char === '(') tokens.push({ kind: 'open', start: index, end: index + 1 });
    else if (char === ')') tokens.push({ kind: 'close', start: index, end: index + 1 });
    else throw new CalculationError(`“${char}” is not supported.`);
    index += 1;
  }
  return tokens;
}

interface TermValue {
  readonly value: number;
  /** A bare `n%`, which `+`/`-` treat as relative to their left side. */
  readonly percent: boolean;
}

function parse(tokens: readonly CalculatorToken[]): number {
  let position = 0;
  const peek = () => tokens[position];

  const expression = (): number => {
    let left = term().value;
    for (let token = peek(); token?.kind === 'operator' && (token.op === '+' || token.op === '-'); token = peek()) {
      position += 1;
      const right = term();
      const amount = right.percent ? left * right.value : right.value;
      left = token.op === '+' ? left + amount : left - amount;
    }
    return left;
  };

  const term = (): TermValue => {
    let current = unary();
    for (let token = peek(); token?.kind === 'operator' && (token.op === '*' || token.op === '/'); token = peek()) {
      position += 1;
      const right = unary().value;
      if (token.op === '/' && right === 0) throw new CalculationError('Cannot divide by zero.');
      current = { value: token.op === '*' ? current.value * right : current.value / right, percent: false };
    }
    return current;
  };

  const unary = (): TermValue => {
    const token = peek();
    if (token?.kind === 'operator' && (token.op === '+' || token.op === '-')) {
      position += 1;
      const inner = unary();
      return { value: token.op === '-' ? -inner.value : inner.value, percent: inner.percent };
    }
    const value = primary();
    if (peek()?.kind === 'percent') {
      position += 1;
      return { value: value / 100, percent: true };
    }
    return { value, percent: false };
  };

  const primary = (): number => {
    const token = peek();
    if (token === undefined) throw new CalculationError('The expression is incomplete.');
    if (token.kind === 'number') {
      position += 1;
      return token.value;
    }
    if (token.kind === 'open') {
      position += 1;
      const value = expression();
      if (peek()?.kind !== 'close') throw new CalculationError('A “(” is not closed.');
      position += 1;
      return value;
    }
    throw new CalculationError('The expression is incomplete.');
  };

  const value = expression();
  const rest = peek();
  if (rest !== undefined) {
    throw new CalculationError(rest.kind === 'close' ? 'There is an extra “)”.' : 'Add an operator between values.');
  }
  return value;
}

function rounded(value: number): number {
  const result = Number(value.toPrecision(SIGNIFICANT_DIGITS));
  return Object.is(result, -0) ? 0 : result;
}

/** Evaluates arithmetic locally and deterministically. Never throws. */
export function evaluateCalculation(expression: string): CalculationEvaluation {
  if (expression.trim().length === 0) return { ok: false, error: 'Enter a calculation.' };
  if (expression.length > CALCULATOR_EXPRESSION_LIMIT) return { ok: false, error: 'The calculation is too long.' };
  try {
    const value = parse(tokenize(expression));
    if (!Number.isFinite(value)) return { ok: false, error: 'The result is too large.' };
    return { ok: true, value: rounded(value) };
  } catch (caught) {
    if (caught instanceof CalculationError) return { ok: false, error: caught.message };
    throw caught;
  }
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 10, useGrouping: true });

/** Locale-independent display: `5000` → `5,000`, `0.30000000000000004` stays `0.3` after evaluation. */
export function formatCalculationNumber(value: number): string {
  if (Object.is(value, -0)) return '0';
  if (Math.abs(value) >= 1e21) return String(value);
  return NUMBER_FORMAT.format(value);
}

function isUnaryAt(tokens: readonly CalculatorToken[], index: number): boolean {
  const previous = tokens[index - 1];
  return previous === undefined || previous.kind === 'operator' || previous.kind === 'open';
}

const DISPLAY_OPERATOR: Readonly<Record<Operator, string>> = { '+': '+', '-': '-', '*': '×', '/': '÷' };

/**
 * Readable form of an expression: `1250*4` → `1,250 × 4`. When a unit is given
 * and the expression only adds or subtracts plain quantities, each quantity
 * carries the unit (`2,450 mm - 36 mm`); otherwise the unit belongs to the
 * result alone. Returns the trimmed input unchanged if it does not tokenize.
 */
export function formatCalculationExpression(expression: string, unit = ''): string {
  let tokens: CalculatorToken[];
  try {
    tokens = tokenize(expression);
  } catch {
    return expression.trim();
  }
  const cleanUnit = normalizedCalculationUnit(unit);
  const unitOnOperands = cleanUnit.length > 0 && tokens.every((token, index) =>
    token.kind === 'number'
    || (token.kind === 'operator' && (token.op === '+' || token.op === '-') && !isUnaryAt(tokens, index)));
  let text = '';
  tokens.forEach((token, index) => {
    switch (token.kind) {
      case 'number':
        text += formatCalculationNumber(token.value) + (unitOnOperands ? ` ${cleanUnit}` : '');
        break;
      case 'operator':
        text += isUnaryAt(tokens, index)
          ? (token.op === '-' ? '-' : '')
          : ` ${DISPLAY_OPERATOR[token.op]} `;
        break;
      case 'percent':
        text += '%';
        break;
      case 'open':
        text += '(';
        break;
      case 'close':
        text += ')';
        break;
    }
  });
  return text.trim();
}

/**
 * Sign change (±) for the last number typed. `12` → `-12`, `-12` → `12`,
 * `5 - 3` → `5 + 3`, `5 × 3` → `5 × -3`. Anything without a trailing number
 * (empty, ends in an operator, invalid) is returned unchanged.
 */
export function toggleLastNumberSign(expression: string): string {
  let tokens: CalculatorToken[];
  try {
    tokens = tokenize(expression);
  } catch {
    return expression;
  }
  const lastIndex = tokens.length - 1;
  const last = tokens[lastIndex];
  if (last?.kind !== 'number') return expression;
  const previous = tokens[lastIndex - 1];
  if (previous?.kind === 'operator' && (previous.op === '+' || previous.op === '-')) {
    const unary = isUnaryAt(tokens, lastIndex - 1);
    if (unary) return expression.slice(0, previous.start) + expression.slice(previous.end);
    const flipped = previous.op === '+' ? '-' : '+';
    return expression.slice(0, previous.start) + flipped + expression.slice(previous.end);
  }
  return `${expression.slice(0, last.start)}-${expression.slice(last.start)}`;
}

function singleLine(value: string, limit: number): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, limit).trim();
}

export function normalizedCalculationLabel(label: string): string {
  return singleLine(label, CALCULATION_LABEL_LIMIT);
}

export function normalizedCalculationUnit(unit: string): string {
  return singleLine(unit, CALCULATION_UNIT_LIMIT);
}

/**
 * One calculation as the owner means it. The label and unit describe this use
 * of the result only; the same arithmetic can be added again with another label.
 */
export interface LabeledCalculation {
  readonly expression: string;
  readonly result: number;
  /** Free text such as “Length of beam for cabinet”. Empty when unlabeled. */
  readonly label: string;
  /** Free text such as “mm”. Empty when unitless. */
  readonly unit: string;
}

export type LabeledCalculationResult =
  | { readonly ok: true; readonly calculation: LabeledCalculation }
  | { readonly ok: false; readonly error: string };

export function labeledCalculation(input: {
  readonly expression: string;
  readonly label?: string;
  readonly unit?: string;
}): LabeledCalculationResult {
  const evaluated = evaluateCalculation(input.expression);
  if (!evaluated.ok) return evaluated;
  return {
    ok: true,
    calculation: {
      expression: input.expression.trim(),
      result: evaluated.value,
      label: normalizedCalculationLabel(input.label ?? ''),
      unit: normalizedCalculationUnit(input.unit ?? ''),
    },
  };
}

/**
 * The single line written into a note:
 *   `Length of beam for cabinet: 2,450 mm - 36 mm = 2,414 mm`
 *   `1,250 × 4 = 5,000`
 */
export function formatCalculationNoteLine(calculation: LabeledCalculation): string {
  const expression = formatCalculationExpression(calculation.expression, calculation.unit);
  const result = formatCalculationNumber(calculation.result) + (calculation.unit ? ` ${calculation.unit}` : '');
  const math = `${expression} = ${result}`;
  return calculation.label ? `${calculation.label}: ${math}` : math;
}

export interface NoteSelection {
  readonly start: number;
  readonly end: number;
}

export interface NoteLineInsertion {
  readonly body: string;
  /** Caret position just after the inserted line. */
  readonly cursor: number;
}

/**
 * Adds `line` to `body` without removing or splitting any existing text.
 *
 * With a selection/caret, the line goes on its own line right after the line
 * holding the caret (a selection is never replaced). Without one, it is
 * appended. A blank current line is reused rather than adding a gap.
 */
export function insertNoteLine(body: string, line: string, selection: NoteSelection | null = null): NoteLineInsertion {
  const caret = selection === null ? body.length : Math.min(Math.max(selection.end, 0), body.length);
  const lineEnd = body.indexOf('\n', caret);
  const position = lineEnd === -1 ? body.length : lineEnd;
  const prefix = position > 0 && body[position - 1] !== '\n' ? '\n' : '';
  const inserted = prefix + line;
  return {
    body: body.slice(0, position) + inserted + body.slice(position),
    cursor: position + inserted.length,
  };
}
